import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { LeadEntityType } from "lib/types/graphQLEnums";
import {
  AddLeadEntitiesMutation,
  AddLeadEntitiesMutationVariables,
} from "../../../generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";
import { updateLeadEntityCache } from "./update_cache/updateLeadEntityCache";
import { leadEntityEventData } from "./leadEntityTracking";

const ADD_LEAD_ENTITY_MUTATION = graphql(`
  mutation addLeadEntities($id: String!, $entities: AddLeadEntitiesInput!) {
    addLeadEntities(id: $id, entities: $entities) {
      leadId
      entityId
      entityType
      leadName
    }
  }
`);

/**
 * @param trackingData - Extra attributes for the "Lead data added" event. A function is called
 * with the succeeding add's variables, for attributes that belong to that one call.
 */
export function useAddLeadEntities(
  trackingData?:
    | EventData
    | ((variables: AddLeadEntitiesMutationVariables) => EventData | undefined),
  options?: UseMutationOptions<
    AddLeadEntitiesMutation,
    unknown,
    AddLeadEntitiesMutationVariables,
    unknown
  >,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();

  return useGraphQlMutation(ADD_LEAD_ENTITY_MUTATION, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const entities = data.addLeadEntities;

      await updateLeadEntityCache({
        queryClient,
        isAdding: true,
        entities,
      });

      const hasCustomBuyer = variables.entities.entities.some(
        (e) => e.entityType === LeadEntityType.CustomOrganisation,
      );

      const callTrackingData =
        typeof trackingData === "function" ? trackingData(variables) : trackingData;
      logEvent(EventNames.leadDataAdded, {
        ...callTrackingData,
        ...leadEntityEventData(
          variables.entities.entities.map((e) => e.entityType),
          callTrackingData,
        ),
        "Lead ID": variables.id,
        "Amount of entities": entities.length,
        "Free-text buyer": hasCustomBuyer,
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
}
